/**
 * ClientContext — Cliente activo del workspace
 *
 * Pattern Monday-like: el usuario selecciona un cliente y TODAS las páginas
 * (Kanban, Tareas, Gantt, Calendario, Coberturas, Proyectos, Banco de Recursos)
 * se filtran automáticamente a ese cliente.
 *
 * Persistencia: localStorage para sobrevivir reloads.
 * Estado "Todos los clientes": clienteId === null.
 */
import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { api } from "../api/client.js";
import { useAuth } from "./AuthContext.jsx";

const ClientContext = createContext(null);

const STORAGE_KEY = "thecrew.selectedClienteId";

export function ClientProvider({ children }) {
  const { user, loading: authLoading } = useAuth();

  const [clientes,    setClientes]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [selectedId,  setSelectedId]  = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });

  /* ── Load clientes (re-runs cuando cambia el user) ───────────────────── */
  const reload = useCallback(async () => {
    if (authLoading) return;           // esperar a que termine de cargar Firebase
    if (!user) {                       // sin usuario logueado, no hay datos
      setClientes([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const res = await api.getClientes();
      setClientes(res.clientes ?? []);
    } catch (e) {
      console.warn("[ClientContext] No se pudieron cargar clientes:", e.message);
      setClientes([]);
    } finally {
      setLoading(false);
    }
  }, [user, authLoading]);

  useEffect(() => { reload(); }, [reload]);

  /* ── Persist selection ───────────────────────────────────────────────── */
  const setSelected = useCallback((id) => {
    setSelectedId(id);
    try {
      if (id) localStorage.setItem(STORAGE_KEY, id);
      else    localStorage.removeItem(STORAGE_KEY);
    } catch {}
  }, []);

  /* ── Helpers ─────────────────────────────────────────────────────────── */
  const selectedCliente = useMemo(() => {
    if (!selectedId) return null;
    return clientes.find(c => c.id === selectedId) ?? null;
  }, [selectedId, clientes]);

  /** Filtro: devuelve true si el item pertenece al cliente seleccionado.
   *  Si no hay cliente seleccionado ("Todos"), devuelve true siempre. */
  const matchesSelected = useCallback((clienteId) => {
    if (!selectedId) return true;
    return clienteId === selectedId;
  }, [selectedId]);

  /** Filter helper para queries al API */
  const apiFilters = useMemo(
    () => selectedId ? { cliente_id: selectedId } : {},
    [selectedId]
  );

  const value = {
    clientes,
    loading,
    selectedId,
    selectedCliente,
    setSelected,
    matchesSelected,
    apiFilters,
    reload,
    isFiltered: !!selectedId,
  };

  return <ClientContext.Provider value={value}>{children}</ClientContext.Provider>;
}

export function useClient() {
  const ctx = useContext(ClientContext);
  if (!ctx) throw new Error("useClient must be used inside <ClientProvider>");
  return ctx;
}
